import { FC, useEffect, useState } from "react";
import "./ModalChatToolInsert.scss";
import ModalDefault from "../../../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Col, FormGroup, Row } from "react-bootstrap";
import ReactSelect from "react-select";
import ErrorMessage from "../../../../components/ErrorMessage/ErrorMessage";
import { customStyles } from "../../../../models/SelectCustomStyles";
import { schema } from "./Validate";
import { useChatTools } from "../../../../providers/Chats/ChatToolsProvider";
import { useAgentsCategories } from "../../../../providers/Agents/AgentsCategories/AgentsCategoriesProvider";
import { useSections } from "../../../../providers/Articles/Sections/SectionsProvider";
import { ChatToolModel } from "../../../../models/Chat/ChatTool/ChatToolModel";

interface ModalChatToolInsertProps {
  show: boolean;
  onClose: () => void;
}

const DEFAULT_PARAMETERS = JSON.stringify(
  {
    type: "object",
    properties: {},
    required: [],
  },
  null,
  2
);

const ModalChatToolInsert: FC<ModalChatToolInsertProps> = ({
  show,
  onClose,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [parametersText, setParametersText] = useState<string>(DEFAULT_PARAMETERS);
  const [parametersError, setParametersError] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [selectedSection, setSelectedSection] = useState<any | null>(null);

  const { tool, setTool, handleList, handleFetch } = useChatTools();
  const { categoriesList, handleListCategories } = useAgentsCategories();
  const {
    sectionsOptions,
    setParams: setParamsSections,
    setSectionsOptions,
  } = useSections();
  const { onInvalid } = useAuth();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const categoriesOptions =
    categoriesList?.map((item: any) => ({
      label: item.name,
      value: item.id,
    })) ?? [];

  const clearFields = () => {
    reset();
    setValue("name", "");
    setValue("description", "");
    setParametersText(DEFAULT_PARAMETERS);
    setParametersError("");
    setSelectedCategory(null);
    setSelectedSection(null);
  };

  const handleClose = () => {
    clearFields();
    setTool(null);
    onClose();
  };

  useEffect(() => {
    if (show) {
      handleListCategories();

      if (tool?.id) {
        setValue("name", tool?.name ?? "");
        setValue("description", tool?.description ?? "");
        setParametersText(
          tool?.parameters
            ? JSON.stringify(tool.parameters, null, 2)
            : DEFAULT_PARAMETERS
        );
      } else {
        clearFields();
      }
    }
  }, [show]);

  useEffect(() => {
    if (tool?.id && categoriesList?.length) {
      setSelectedCategory(
        categoriesOptions.find((item) => item.value === tool?.category_id) ??
        null
      );
    }
  }, [tool, categoriesList]);

  useEffect(() => {
    if (!!selectedCategory) {
      setParamsSections({
        withPaginate: false,
        filter: { category_id: selectedCategory?.value },
      });
    } else {
      setSelectedSection(null);
      setSectionsOptions([]);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (tool?.id && sectionsOptions?.length) {
      setSelectedSection(
        sectionsOptions.find((item: any) => item.value === tool?.section_id) ??
        null
      );
    }
  }, [tool, sectionsOptions]);

  const onSubmit = async (data: any) => {
    let parsedParameters: any;

    try {
      parsedParameters = parametersText?.trim()
        ? JSON.parse(parametersText)
        : {};
    } catch (e) {
      setParametersError("O campo Parâmetros deve conter um JSON válido.");
      return;
    }

    if (
      typeof parsedParameters !== "object" ||
      Array.isArray(parsedParameters) ||
      parsedParameters === null
    ) {
      setParametersError(
        "O campo Parâmetros deve ser um objeto JSON (JSON Schema)."
      );
      return;
    }

    setParametersError("");
    setIsLoadingSubmit(true);

    const payload: ChatToolModel = {
      id: tool?.id,
      name: data.name,
      description: data.description,
      category_id: selectedCategory?.value ?? null,
      section_id: selectedSection?.value ?? null,
      parameters: parsedParameters,
    };

    const ret = await handleFetch(payload);

    if (ret) {
      toast.success(
        tool?.id
          ? "Ferramenta alterada com sucesso!"
          : "Ferramenta salva com sucesso!"
      );
      handleList();
      handleClose();
    }

    setIsLoadingSubmit(false);
  };

  return (
    <ModalDefault
      title={!!tool?.id ? "Editar Ferramenta" : "Adicionar Ferramenta"}
      icon="article"
      show={show}
      onClose={handleClose}
      sizeModal={"lg"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalChatToolInsert" data-testid="ModalChatToolInsert">
        <Row>
          <Col md={12}>
            <FormGroup className="form-group">
              <label htmlFor="name">NOME *</label>
              <input
                id="name"
                className={`form-control ${onInvalid(errors?.name)}`}
                type="text"
                {...register("name")}
                autoFocus
              />
              <ErrorMessage
                name={"Nome"}
                type={errors?.name?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup className="form-group">
              <label htmlFor="description">DESCRIÇÃO *</label>
              <textarea
                id="description"
                rows={10}
                className={`form-control ${onInvalid(errors?.description)}`}
                {...register("description")}
                style={{ fontFamily: "monospace", resize: "vertical" }}
              />
              <ErrorMessage
                name={"Descrição"}
                type={errors?.description?.type?.toString()}
                min={3}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup className="form-group">
              <label htmlFor="category_id">CATEGORIA</label>
              <ReactSelect
                isSearchable
                isClearable
                options={categoriesOptions}
                value={selectedCategory}
                placeholder="Selecione"
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(e) => setSelectedCategory(e)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup className="form-group">
              <label htmlFor="section_id">SESSÃO</label>
              <ReactSelect
                isSearchable
                isClearable
                options={sectionsOptions ?? []}
                value={selectedSection}
                placeholder="Selecione"
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(e) => setSelectedSection(e)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup className="form-group">
              <label htmlFor="parameters">PARÂMETROS (JSON Schema) *</label>
              <textarea
                id="parameters"
                rows={12}
                className={`form-control ${parametersError ? "is-invalid" : ""}`}
                value={parametersText}
                onChange={(e) => {
                  setParametersText(e.target.value);
                  setParametersError("");
                }}
                style={{ fontFamily: "monospace", resize: "vertical" }}
              />
              {parametersError && (
                <small className="text-danger">{parametersError}</small>
              )}
            </FormGroup>
          </Col>
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ModalChatToolInsert;